import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Layout from '@/components/Layout/Layout';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { IproductResponse } from '@/interfaces/productresponse.interface';
import { showToastNotification, ThrowDateTimeError } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { errorTypes } from '@/utility/validation';
import BrandTable from '@/components/category_brands/BrandTable';
import CategoryTable from '@/components/category_brands/CategoryTable';
import { IBrandsResponse } from '@/interfaces/brandsResponse.interface';
import { ICategoryData } from '@/interfaces/categoryResponse.interface';
import ProductTable from '@/components/product/ProductTable';
import ErrorMessage from '@/components/common/ErrorMessage';

interface AddProductDiscountProp {
  id: string[];
  discountPercentage: number;
  discountStartDate: string | null;
  discountEndDate: string | null;
  type: 'add';
  label: string;
  brandId: string;
  categoryId: string;
  target: 'standard' | 'employee';
}

const AddProductDiscount = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string | null>(null);

  const [selectedData, setSelectedData] = useState<
    IBrandsResponse[] | ICategoryData[] | IproductResponse[]
  >([]);

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    getValues,
    control,
    formState: { errors },
  } = useForm<AddProductDiscountProp>({
    defaultValues: {
      id: [],
      discountPercentage: 1,
      discountStartDate: null,
      discountEndDate: null,
      type: 'add',
      target: 'standard',
    },
  });
  const [target, discountStartDate, discountEndDate] = watch([
    'target',
    'discountStartDate',
    'discountEndDate',
  ]);
  const brandsIds = getValues('brandId');
  const categoryIds = getValues('categoryId');
  const productIds = getValues('id');

  const [redirection, setRedirection] = useState<string>(
    brandsIds ? 'product' : categoryIds ? 'category' : 'product',
  );

  useEffect(() => {
    if ((!discountStartDate || !discountEndDate) && target === 'standard') {
      console.log('condation true');
      setDateError('Discount dates are required');
      return;
    } else {
      setDateError(null);
    }
    if (discountStartDate && discountEndDate) {
      const start = new Date(discountStartDate);
      const end = new Date(discountEndDate);
      setDateError(ThrowDateTimeError(start, end));
    }
  }, [discountStartDate, discountEndDate, target]);

  const onSubmit = async (data: AddProductDiscountProp) => {
    if (dateError) {
      showToastNotification('danger', dateError);
      return;
    }
    if (target === 'employee' && selectedData.length === 0) {
      showToastNotification('warning', 'Please select atleast one product');
      return;
    }
    try {
      setIsLoading(true);
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/create/discount`;
      const payload = {
        ...data,
        id: redirection === 'product' ? selectedData.map((i) => i.id) : null,
        categoryId: redirection === 'category' ? selectedData[0]?.id : null,
        brandId: redirection === 'brand' ? selectedData[0]?.id : null,
      };
      await callApi(PATH, { method: 'POST', data: payload });
      setIsLoading(false);
      navigate(RoutesLink.discounts);
    } catch (error: unknown) {
      const err = error as { message?: string };
      if (err.message) showToastNotification('danger', err.message);
      setIsLoading(false);
      console.log('error in discount upload', error);
    }
  };

  const tabs = [
    { value: 'standard', label: 'Standard' },
    { value: 'employee', label: 'Employee Only' },
  ];

  return (
    <Layout>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="space-y-6">
          <Controller
            control={control}
            name="target"
            render={({ field }) => (
              <div className="relative bg-primary-light/30 w-fit p-1 rounded-lg flex items-center text-sm">
                {/* Sliding Background */}
                <div
                  className={`absolute top-1 bottom-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-in-out
                ${target === 'standard' ? 'left-1 w-23' : 'left-24.25 w-30.25'}
              `}
                />

                {tabs.map((i) => {
                  const active = target === i.value;

                  return (
                    <button
                      type="button"
                      key={i.value}
                      onClick={() => field.onChange(i.value)}
                      className={`
                    relative z-10 px-4 py-1.5 rounded-md transition-colors duration-300
                    ${active ? 'text-black font-medium' : 'text-muted-foreground'}
                  `}
                    >
                      {i.label}
                    </button>
                  );
                })}
              </div>
            )}
          />
          <Card>
            <h2 className="text-lg font-semibold mb-3">Discount Details</h2>
            {target === 'standard' && (
              <div className="mb-3">
                <FormField
                  label="Label"
                  placeholder="EID Special"
                  {...register(`label`, {
                    ...errorTypes.required('Discount Label'),
                  })}
                />
                <ErrorMessage message={errors.label?.message} />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <FormField
                  type="number"
                  onWheel={(e) => e.currentTarget.blur()}
                  label="Enter Discount"
                  placeholder="Select discounted products"
                  suffix="%"
                  min={1}
                  step="0.01"
                  {...register('discountPercentage', {
                    valueAsNumber: true,
                    required: 'Discount is required',
                  })}
                />
              </div>
              <div>
                {target === 'standard' ? (
                  <FormField
                    label="Discount Type"
                    type="select"
                    value={redirection}
                    options={[
                      { value: 'product', label: 'Product' },
                      { value: 'brand', label: 'Brand' },
                      { value: 'category', label: 'Category' },
                    ]}
                    onChange={(v) => setRedirection(v.target.value)}
                  />
                ) : (
                  <FormField
                    label="Discount Type"
                    value={redirection}
                    disabled
                    readOnly
                  />
                )}
              </div>
            </div>
          </Card>
          {redirection === 'brand' ? (
            <BrandTable
              selectionMode="single"
              initialSelectedIds={brandsIds ? [brandsIds] : undefined}
              onSelectBrand={setSelectedData}
            />
          ) : redirection === 'category' ? (
            <CategoryTable
              selectionMode="single"
              initialSelectedIds={categoryIds ? [categoryIds] : undefined}
              onSelectCtegory={setSelectedData}
            />
          ) : (
            <ProductTable
              selectionMode="multiple"
              initialSelectedIds={productIds ? productIds : undefined}
              onSelectProducts={setSelectedData}
            />
          )}
        </div>
        {target === 'standard' && (
          <Card>
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-4">Active dates</h2>

              <div className="grid grid-cols-2 gap-2">
                <FormField
                  label="Start Date"
                  type="date"
                  defaultValue={watch('discountStartDate')?.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time =
                      watch('discountStartDate')?.split('T')[1] || '00:00';
                    setValue('discountStartDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
                <FormField
                  label="Start Time"
                  type="time"
                  defaultValue={
                    watch('discountStartDate')?.split('T')[1] || '00:00'
                  }
                  onChange={(e) => {
                    const time = e.target.value;
                    const date =
                      watch('discountStartDate')?.split('T')[0] ||
                      new Date().toISOString().split('T')[0];
                    setValue('discountStartDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <FormField
                  label="End Date"
                  type="date"
                  defaultValue={watch('discountEndDate')?.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time =
                      watch('discountEndDate')?.split('T')[1] || '23:59';
                    setValue('discountEndDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />

                {/* Time */}
                <FormField
                  label="End Time"
                  type="time"
                  defaultValue={
                    watch('discountEndDate')?.split('T')[1] || '23:59'
                  }
                  onChange={(e) => {
                    const time = e.target.value;
                    const date =
                      watch('discountEndDate')?.split('T')[0] ||
                      new Date().toISOString().split('T')[0];
                    setValue('discountEndDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
              </div>
              <ErrorMessage message={dateError!} />
            </div>
          </Card>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {isLoading ? <BtnLoader /> : 'Save'}
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default AddProductDiscount;
